import React, { useEffect, useState } from 'react';
import { useApiQuery } from '@orderx/lab/xr/http';
import { apiPublicLocationLocationIdGet } from '../../api/public.location.{locationId}.[get]';
import { useRouter } from '../../hooks/router';
import { XLoader } from '../../components/x/loader/XLoader';
import { UiGrid, UiInput } from '@open-stack/ui';
import { RootIdPage } from '../id/RootIdPage';
import { Location } from '../../XV2';
import { LocationCard } from '../../components/location/card/LocationCard';
import { Outlet } from '../../XV2';
import { useZoneHistory } from '../../hooks/zone-history';
import { BrowsePage } from '../browse/BrowsePage';
import { XPageHeader } from '../../components/x/page/header/XPageHeader';
import { XRouterRedirect } from '../../components/x/router/redirect/XRouterRedirect';
import { XButton } from '@orderx/lab/ui';
import { stringIncludes } from '../../util/stringIncludes';

export function LocationPage() {
    const {
        location: { pathname },
        params: { locationId },
        query,
    } = useRouter();
    const [outlet, setOutlet] = useState<Outlet>();
    const [zoneId, setZoneId] = useState<string | number>();
    const [keyword, setKeyword] = useState('');
    const zoneHistory = useZoneHistory();

    const { run, response: location } = useApiQuery<Location>(() =>
        apiPublicLocationLocationIdGet({
            params: { locationId },
        }),
    );

    useEffect(() => {
        run();
    }, []);

    useEffect(() => {
        if (location) {
            const queryOutlet = query.outletId
                ? location.outlets.find((outlet) => outlet.id === Number(query.outletId))
                : undefined;

            if (queryOutlet) {
                setOutlet(queryOutlet);
            } else if (location.outlets?.length === 1) {
                const outlet = location.outlets[0];

                setOutlet(outlet);

                if (outlet?.zones?.length === 1) {
                    setZoneId(outlet.zones[0].hash);
                }
            }
        }
    }, [location, query]);

    useEffect(() => {
        setKeyword('');
    }, [outlet]);

    if (!location) {
        return <XLoader />;
    }

    function handleOutletClick(outlet: Outlet) {
        if (outlet.zones.length === 1) {
            setZoneId(outlet.zones[0].hash);
        } else {
            setOutlet(outlet);
        }
    }

    const hasBackButton = !outlet || location.outlets.length === 1;

    if (zoneId) {
        return <XRouterRedirect pageOrPath={RootIdPage} params={{ id: zoneId }} />;
    }

    return (
        <>
            <XPageHeader
                title={outlet ? outlet.name : location.name}
                backButtonTo={hasBackButton && BrowsePage.route}
                backButtonOnClick={() => {
                    if (outlet) {
                        setOutlet(undefined);
                    }
                }}
            />

            <UiGrid padding={2}>
                {outlet ? (
                    <>
                        {outlet.zones.length > 100 && (
                            <UiInput value={keyword} onChangeValue={setKeyword} placeholder={'Type to search'} />
                        )}

                        <UiGrid minMaxFill={100} gap={1}>
                            {outlet.zones
                                ?.filter((zone) => stringIncludes(zone.name, keyword))
                                .map((zone) => (
                                    <XButton
                                        to={[RootIdPage.route, { id: zone.hash }]}
                                        onClick={() => zoneHistory.setPath(zone.id, pathname)}
                                        key={zone.id}
                                        fullWidth>
                                        {zone.name}
                                    </XButton>
                                ))}
                        </UiGrid>
                    </>
                ) : (
                    location.outlets.map((outlet) => (
                        <LocationCard
                            name={outlet.name}
                            description={outlet.description}
                            image={outlet.image}
                            isClosed={outlet.isClosed}
                            onClick={() => handleOutletClick(outlet)}
                            key={outlet.id}
                        />
                    ))
                )}
            </UiGrid>
        </>
    );
}

LocationPage.route = '/location/:locationId';
